/**
 * SpriteRenderer - 64x64 Handcrafted Isometric Pixel/Procedural Sprite Engine
 * Directly ported from the refined prototype specifications.
 */

import { EntityState, Direction8 } from '../../core/Types';

export class SpriteRenderer {
  /**
   * Main entry for drawing any entity with full prototype art rules
   */
  public drawEntity(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    ctx.save();

    // 1. Soft Drop Shadow
    this.drawShadow(ctx, ent, sx, sy);

    // 2. Dead Entity Render
    if (ent.isDead) {
      this.drawCorpse(ctx, ent, sx, sy);
      ctx.restore();
      return;
    }

    // 3. Stun / Movement Shake
    if (ent.cc && ent.cc.type === 'stun') {
      const shake = (Math.random() - 0.5) * 3;
      ctx.translate(shake, 0);
    }

    // 4. Buff Auras (Ward Bastion, Hymn of Vigor)
    this.drawBuffAuras(ctx, ent, sx, sy, time);

    // 5. Draw Entity Body based on Role (Supports Dynamic Player Switching & World NPCs)
    if (ent.category === 'player' || ent.category === 'companion' || ent.category === 'npc') {
      this.drawHeroByRole(ctx, ent, sx, sy, time);
      // If active player, draw Leader Crown (unless already wearing King Lord British crown on head)
      const isLordBritishAdmin =
        ent.role === 'admin' ||
        (ent as any).isAdmin === true ||
        (ent.job && (ent.job.includes('관리자') || ent.job.includes('admin') || ent.job.includes('국왕') || ent.job.includes('Lord British'))) ||
        ent.appearance?.isKing;
      if (ent.category === 'player' && !isLordBritishAdmin) {
        this.drawLeaderCrown(ctx, sx, ent.isMounted ? sy - 14 : sy, time);
      }
    } else if (ent.category === 'monster') {
      this.drawMonster(ctx, ent, sx, sy, time);
    }

    // 6. Hurt Flash Overlay
    // (If hurt recently, subtle flash)

    ctx.restore();
  }

  private drawLeaderCrown(ctx: CanvasRenderingContext2D, sx: number, sy: number, time: number): void {
    const floatY = Math.sin(time * 0.005) * 3;
    const cy = sy - 48 + floatY;
    // Glowing golden halo
    ctx.save();
    ctx.fillStyle = 'rgba(251, 191, 36, 0.3)';
    ctx.beginPath();
    ctx.arc(sx, cy - 2, 9, 0, Math.PI * 2);
    ctx.fill();

    // Golden Crown Icon
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('👑', sx, cy);
    ctx.restore();
  }

  /**
   * Public preview renderer for Character Creation & Customization Studio (2.5x zoomed canvas)
   */
  public drawCharacterPreview(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    cx: number,
    cy: number,
    zoomScale: number,
    time: number
  ): void {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(zoomScale, zoomScale);
    this.drawShadow(ctx, ent, 0, 0);
    this.drawBuffAuras(ctx, ent, 0, 0, time);
    this.drawHeroByRole(ctx, ent, 0, 0, time);
    ctx.restore();
  }

  private drawHeroByRole(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    const scale = ent.appearance?.bodyScale || 1.0;
    ctx.save();
    if (Math.abs(scale - 1.0) > 0.01) {
      ctx.translate(sx, sy);
      ctx.scale(scale, scale);
      ctx.translate(-sx, -sy);
    }

    let riderY = sy;
    if (ent.isMounted) {
      const isMoving = Math.abs(ent.vx) > 0.05 || Math.abs(ent.vy) > 0.05;
      const gallopBob = isMoving ? Math.sin(time * 0.022) * 2.5 : Math.sin(time * 0.005) * 0.8;
      this.drawWarhorseMount(ctx, ent, sx, sy, time, isMoving, gallopBob);
      riderY = sy - 13 + gallopBob;
    }

    // 관리자(admin), 국왕, 로드 브리티시 캐릭터는 시그니처 외형(왕관, 붉은 벨벳 망토, 황금 홀 Scepter)으로 렌더링!
    const isLordBritishAdmin =
      ent.role === 'admin' ||
      (ent as any).isAdmin === true ||
      (ent.job && (ent.job.includes('관리자') || ent.job.includes('admin') || ent.job.includes('국왕') || ent.job.includes('Lord British'))) ||
      ent.appearance?.isKing;

    if (isLordBritishAdmin) {
      this.drawKingLordBritish(ctx, ent, sx, riderY, time);
    } else if (ent.job?.includes('무직') || ent.job?.includes('성인식') || ent.job?.includes('초보자')) {
      // 무직 / 초보자 기본 전신: 남자는 팬티, 여자는 브래지어 & 팬티 (기본 황토색)
      this.drawUnderwearNovice(ctx, ent, sx, riderY, time);
    } else if (ent.role === 'tank') {
      this.drawArthurPaladin(ctx, ent, sx, riderY, time);
    } else {
      this.drawCompanion(ctx, ent, sx, riderY, time);
    }
    ctx.restore();
  }

  /**
   * Handcrafted Pixel-Art Warhorse / Steed Renderer (with animated galloping legs, saddle, bridle & dust)
   */
  private drawWarhorseMount(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number,
    isMoving: boolean,
    gallopBob: number
  ): void {
    const coat = ent.horseColor || '#92400e';
    const bardingColor = ent.appearance?.primaryArmorColor || '#2563eb';
    const trimColor = ent.appearance?.secondaryArmorColor || '#fbbf24';
    const facingLeft = ent.dir === 'W' || ent.dir === 'NW' || ent.dir === 'SW';
    const dirSign = facingLeft ? -1 : 1;

    ctx.save();
    ctx.translate(sx, sy + gallopBob);
    if (facingLeft) {
      ctx.scale(-1, 1);
    }

    // 1. Galloping Hoof Dust Puffs when moving
    if (isMoving) {
      const dustPhase = (time * 0.015) % 1;
      ctx.fillStyle = `rgba(214, 211, 209, ${0.55 * (1 - dustPhase)})`;
      ctx.beginPath();
      ctx.arc(-16 - dustPhase * 12, 2 - dustPhase * 4, 4 + dustPhase * 3, 0, Math.PI * 2);
      ctx.arc(-6 - dustPhase * 8, 4 - dustPhase * 2, 3 + dustPhase * 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // 2. Animated 4 Horse Legs + Hooves
    const stride = isMoving ? Math.sin(time * 0.024) * 7 : 0;
    const strideOpp = isMoving ? Math.cos(time * 0.024) * 7 : 0;
    ctx.strokeStyle = coat;
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';

    // Back legs
    ctx.beginPath();
    ctx.moveTo(-11, -10);
    ctx.lineTo(-13 + stride * 0.6, 2);
    ctx.moveTo(-7, -10);
    ctx.lineTo(-6 - strideOpp * 0.6, 3);
    // Front legs
    ctx.moveTo(9, -10);
    ctx.lineTo(11 - stride * 0.7, 2);
    ctx.moveTo(13, -10);
    ctx.lineTo(15 + strideOpp * 0.7, 3);
    ctx.stroke();

    // Dark hooves
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(-14 + stride * 0.6, 1, 3.5, 2.5);
    ctx.fillRect(-7 - strideOpp * 0.6, 2, 3.5, 2.5);
    ctx.fillRect(10 - stride * 0.7, 1, 3.5, 2.5);
    ctx.fillRect(14 + strideOpp * 0.7, 2, 3.5, 2.5);

    // 3. Flowing Horse Tail
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.ellipse(-19, -14 + (isMoving ? Math.sin(time * 0.02) * 2 : 0), 6, 3, -0.35, 0, Math.PI * 2);
    ctx.fill();

    // 4. Muscular Horse Barrel / Body
    ctx.fillStyle = coat;
    ctx.beginPath();
    ctx.ellipse(0, -13, 16, 8.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // 5. Royal Saddle Blanket / Barding & Golden Trim
    ctx.fillStyle = bardingColor;
    ctx.fillRect(-8, -20, 15, 9);
    ctx.strokeStyle = trimColor;
    ctx.lineWidth = 1.4;
    ctx.strokeRect(-8, -20, 15, 9);

    // Leather Saddle & Stirrup
    ctx.fillStyle = '#451a03';
    ctx.beginPath();
    ctx.ellipse(-1, -20, 7, 3.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // 6. Horse Neck & Proud Head (Facing right in local coords)
    ctx.fillStyle = coat;
    ctx.beginPath();
    ctx.moveTo(9, -16);
    ctx.lineTo(16, -29);
    ctx.lineTo(22, -25);
    ctx.lineTo(15, -11);
    ctx.closePath();
    ctx.fill();

    // Horse Snout / Head
    ctx.beginPath();
    ctx.ellipse(20, -26, 6.5, 4, 0.35, 0, Math.PI * 2);
    ctx.fill();

    // Pointed Ears
    ctx.beginPath();
    ctx.moveTo(15, -29);
    ctx.lineTo(17, -35);
    ctx.lineTo(19, -29);
    ctx.closePath();
    ctx.fill();

    // Horse Eye & Golden Bridle Reins
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(19, -28, 2, 2);

    ctx.strokeStyle = trimColor;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.moveTo(16, -28);
    ctx.lineTo(23, -24);
    ctx.lineTo(4, -21);
    ctx.stroke();

    ctx.restore();
    void dirSign;
  }

  /**
   * Renders customizable head, skin tone, eyes (angle/size), hair style, and facial features
   */
  private drawCustomHeadAndHair(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    px: number,
    py: number
  ): void {
    const app = ent.appearance;
    const skinColor = app?.skinColor || '#fde68a';
    const hairColor = app?.hairColor || '#78350f';
    const hairStyle = app?.hairStyle || 'short';
    const facial = app?.gender === 'female' ? 'none' : (app?.facialFeature || 'none');
    const eyeColor = app?.eyeColor || '#0f172a';
    const eyeAngle = app?.eyeAngle || 'normal';
    const eyeSize = app?.eyeSize || 'medium';

    // 1. Back hair layer for long / ponytail / braids / twin_tail / wavy_long
    ctx.fillStyle = hairColor;
    if (hairStyle === 'long') {
      ctx.fillRect(px - 8, py - 31, 16, 17);
    } else if (hairStyle === 'wavy_long') {
      ctx.beginPath();
      ctx.ellipse(px - 6, py - 24, 7, 14, 0.2, 0, Math.PI * 2);
      ctx.ellipse(px + 6, py - 24, 7, 14, -0.2, 0, Math.PI * 2);
      ctx.fill();
    } else if (hairStyle === 'ponytail') {
      ctx.beginPath();
      ctx.ellipse(px - 9, py - 27, 4, 8, -0.4, 0, Math.PI * 2);
      ctx.fill();
    } else if (hairStyle === 'twin_tail') {
      ctx.beginPath();
      ctx.ellipse(px - 10, py - 24, 4, 11, -0.3, 0, Math.PI * 2);
      ctx.ellipse(px + 10, py - 24, 4, 11, 0.3, 0, Math.PI * 2);
      ctx.fill();
    } else if (hairStyle === 'braids') {
      ctx.fillRect(px - 8, py - 26, 3.5, 13);
      ctx.fillRect(px + 4.5, py - 26, 3.5, 13);
    } else if (hairStyle === 'bob_cut') {
      ctx.fillRect(px - 8, py - 28, 16, 11);
    }

    // 2. Head circle
    ctx.fillStyle = skinColor;
    ctx.beginPath();
    ctx.arc(px, py - 26, 7, 0, Math.PI * 2);
    ctx.fill();

    // 3. Eyes (Shape, Size, Angle custom)
    const eyeW = eyeSize === 'small' ? 1.6 : eyeSize === 'large' ? 3.0 : 2.2;
    const eyeH = eyeSize === 'small' ? 1.4 : eyeSize === 'large' ? 2.8 : 2.0;
    const yOffL = eyeAngle === 'sharp' ? 0.6 : eyeAngle === 'droopy' ? -0.6 : 0;
    const yOffR = eyeAngle === 'sharp' ? 0.6 : eyeAngle === 'droopy' ? -0.6 : 0;

    ctx.fillStyle = eyeColor;
    ctx.fillRect(px - 4, py - 27 + yOffL, eyeW, eyeH);
    ctx.fillRect(px + 2, py - 27 + yOffR, eyeW, eyeH);

    // Eye highlight glint for large eyes
    if (eyeSize === 'large') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 3.5, py - 27 + yOffL, 1, 1);
      ctx.fillRect(px + 2.5, py - 27 + yOffR, 1, 1);
    }

    // 4. Facial Features (Male only)
    if (facial === 'scar') {
      ctx.strokeStyle = '#be123c';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(px + 1, py - 30);
      ctx.lineTo(px + 4, py - 22);
      ctx.stroke();
    } else if (facial === 'tattoo') {
      ctx.strokeStyle = '#38bdf8';
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(px - 5, py - 25);
      ctx.lineTo(px - 2, py - 23);
      ctx.stroke();
    } else if (facial === 'beard') {
      ctx.fillStyle = hairColor;
      ctx.beginPath();
      ctx.moveTo(px - 5, py - 23);
      ctx.lineTo(px, py - 15);
      ctx.lineTo(px + 5, py - 23);
      ctx.closePath();
      ctx.fill();
    } else if (facial === 'mustache') {
      ctx.fillStyle = hairColor;
      ctx.fillRect(px - 4, py - 23, 8, 2.2);
    }

    // 5. Top Hair / Bangs
    ctx.fillStyle = hairColor;
    if (hairStyle === 'short' || hairStyle === 'long' || hairStyle === 'ponytail' || hairStyle === 'braids' || hairStyle === 'twin_tail' || hairStyle === 'wavy_long') {
      ctx.beginPath();
      ctx.arc(px, py - 28, 7.3, Math.PI, Math.PI * 2);
      ctx.fill();
      // Bangs
      ctx.fillRect(px - 6, py - 29, 12, 3);
    } else if (hairStyle === 'bob_cut') {
      ctx.beginPath();
      ctx.arc(px, py - 28, 7.4, Math.PI, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(px - 7, py - 29, 14, 4);
    } else if (hairStyle === 'pixie') {
      ctx.beginPath();
      ctx.arc(px, py - 28, 7.5, Math.PI * 0.9, Math.PI * 2.1);
      ctx.fill();
    } else if (hairStyle === 'mohawk') {
      ctx.fillRect(px - 2.5, py - 38, 5, 11);
    } else if (hairStyle === 'tonsure') {
      ctx.fillRect(px - 7, py - 29, 14, 2.5);
    }
  }

  private getLungeOffset(dir: Direction8, dist: number): { x: number; y: number } {
    switch (dir) {
      case 'E': return { x: dist, y: 0 };
      case 'W': return { x: -dist, y: 0 };
      case 'N': return { x: 0, y: -dist * 0.7 };
      case 'S': return { x: 0, y: dist * 0.7 };
      case 'NE': return { x: dist * 0.7, y: -dist * 0.5 };
      case 'NW': return { x: -dist * 0.7, y: -dist * 0.5 };
      case 'SE': return { x: dist * 0.7, y: dist * 0.5 };
      case 'SW': return { x: -dist * 0.7, y: dist * 0.5 };
      default: return { x: dist * 0.7, y: 0 };
    }
  }

  private drawShadow(ctx: CanvasRenderingContext2D, ent: EntityState, sx: number, sy: number): void {
    const isBoss = ent.id.includes('troll') || ent.id.includes('dragon');
    const rx = isBoss ? 24 : 14;
    const ry = isBoss ? 12 : 7;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.beginPath();
    ctx.ellipse(sx, sy + 12, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawCorpse(ctx: CanvasRenderingContext2D, ent: EntityState, sx: number, sy: number): void {
    ctx.fillStyle = '#64748b';
    ctx.fillRect(sx - 12, sy + 4, 24, 6);
    ctx.fillStyle = '#94a3b8';
    ctx.beginPath();
    ctx.arc(sx - 10, sy + 6, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = 'bold 10px sans-serif';
    ctx.fillStyle = '#ef4444';
    ctx.textAlign = 'center';
    ctx.fillText('💀 사망', sx, sy - 6);
  }

  private drawBuffAuras(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    // Ward Bastion: Blue crystalline sphere
    if (ent.buffs.some((b) => b.id === 'ward_bastion')) {
      ctx.save();
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.85)';
      ctx.fillStyle = 'rgba(56, 189, 248, 0.18)';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(sx, sy - 14, 24, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // Hymn of Vigor: Green pulse ellipse on floor
    if (ent.buffs.some((b) => b.id === 'hymn_of_vigor')) {
      ctx.save();
      const pulse = Math.sin(time * 0.006) * 3;
      ctx.strokeStyle = 'rgba(34, 197, 94, 0.7)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(sx, sy + 10, 16 + pulse, 8 + pulse * 0.5, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  /**
   * King Lord British (전능한 국왕 - 로드 브리티시) Handcrafted Isometric Sprite Renderer
   * - Iconic Golden Crown with inset Red Rubies & Star Sapphires
   * - Radiant Royal Scepter (황금 홀) with Pulsing Holy Star Sapphire Gem
   * - Royal Crimson Velvet Mantle with White Ermine Fur Collar (로드 브리티시 시그니처)
   * - Gilded Royal Breastplate with Solaris Sun Crest
   * - Golden Royal Orb (Globus Cruciger) held in left hand
   * - Glowing Divine Aura
   */
  private drawKingLordBritish(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    // Walking bob
    const isMoving = Math.abs(ent.vx) > 0.1 || Math.abs(ent.vy) > 0.1;
    const bob = isMoving ? Math.sin(time * 0.015) * 2 : 0;
    const by = sy + bob;

    // Attack Motion Dynamics (Commanding Scepter Sweep / Cast)
    const animTimer = ent.attackAnimTimer || 0;
    const attackProgress = animTimer > 0 ? Math.min(1, Math.max(0, animTimer / 0.28)) : 0;
    const attackRatio = animTimer > 0 ? Math.sin((1 - attackProgress) * Math.PI) : 0;

    // Lunge forward in direction facing
    const lunge = attackRatio > 0 ? this.getLungeOffset(ent.dir, attackRatio * 7) : { x: 0, y: 0 };
    const px = sx + lunge.x;
    const py = by + lunge.y;

    const app = ent.appearance;
    const capeColor = app?.capeColor || '#991b1b'; // Deep royal velvet crimson
    const goldArmor = '#fbbf24'; // Radiant Gold
    const deepGold = '#d97706'; // Shaded Gold
    const jewelRed = '#ef4444'; // Ruby
    const jewelBlue = '#38bdf8'; // Sapphire

    // 0. Divine Kingly Halo / Sun Aura (Pulsing behind King)
    ctx.save();
    const auraPulse = 0.22 + Math.sin(time * 0.005) * 0.08;
    ctx.fillStyle = `rgba(251, 191, 36, ${auraPulse})`;
    ctx.beginPath();
    ctx.ellipse(px, py - 18, 22, 28, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 1. Royal Mantle / Velvet Cape (Long flowing royal cape)
    ctx.fillStyle = capeColor;
    ctx.fillRect(px - 14, py - 26, 28, 32);

    // Cape Golden Trim Borders
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(px - 14, py - 26, 28, 32);

    // 2. Armored Greaves & Gilded Royal Boots
    ctx.fillStyle = deepGold;
    ctx.fillRect(px - 7, py + 4, 6, 9);
    ctx.fillRect(px + 2, py + 4, 6, 9);
    ctx.fillStyle = '#b45309';
    ctx.fillRect(px - 8, py + 11, 7, 4);
    ctx.fillRect(px + 2, py + 11, 7, 4);
    // Gold boot buckles
    ctx.fillStyle = '#fef08a';
    ctx.fillRect(px - 6, py + 10, 3, 2);
    ctx.fillRect(px + 4, py + 10, 3, 2);

    // 3. Gilded Royal Plate Armor & Regalia Tunic
    ctx.fillStyle = goldArmor;
    ctx.fillRect(px - 9, py - 24, 18, 24);
    // Royal Lion / Sun Crest on Breastplate
    ctx.fillStyle = deepGold;
    ctx.beginPath();
    ctx.arc(px, py - 14, 5.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = jewelRed; // Ruby Heart in the Sun
    ctx.fillRect(px - 1.5, py - 15.5, 3, 3);
    // Gold sash & royal belt
    ctx.fillStyle = '#fde047';
    ctx.fillRect(px - 9, py - 6, 18, 3.5);
    ctx.fillStyle = jewelBlue;
    ctx.fillRect(px - 2, py - 6, 4, 3.5);

    // 4. White Ermine Fur Collar (로드 브리티시 시그니처 담비 모피 망토 깃)
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(px - 15, py - 27, 30, 8);
    // Ermine black tail tufts
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(px - 12, py - 24, 2, 2.5);
    ctx.fillRect(px - 5, py - 23, 2, 2.5);
    ctx.fillRect(px + 3, py - 23, 2, 2.5);
    ctx.fillRect(px + 10, py - 24, 2, 2.5);

    // 5. Head: Custom Face, Hair & Beard (Noble features)
    this.drawCustomHeadAndHair(ctx, ent, px, py - 5);

    // 6. The Crown of Britannia / Solaris (정교한 황금 왕관)
    // Sits directly on King's brow/head
    const crownY = py - 37;
    // Crown velvet cap under arches
    ctx.fillStyle = '#7f1d1d';
    ctx.beginPath();
    ctx.arc(px, crownY + 2, 6.5, Math.PI, Math.PI * 2);
    ctx.fill();

    // Golden Crown Base Circlet
    ctx.fillStyle = goldArmor;
    ctx.fillRect(px - 8, crownY, 16, 5);

    // 5 Crown Points / Peaks (Cross in middle, spires on sides)
    ctx.beginPath();
    // Center tall cross point
    ctx.moveTo(px - 1.5, crownY);
    ctx.lineTo(px - 1.5, crownY - 8);
    ctx.lineTo(px + 1.5, crownY - 8);
    ctx.lineTo(px + 1.5, crownY);
    // Center cross crossbar
    ctx.rect(px - 4, crownY - 7, 8, 2);
    // Left peaks
    ctx.moveTo(px - 8, crownY);
    ctx.lineTo(px - 7, crownY - 5);
    ctx.lineTo(px - 5, crownY);
    ctx.moveTo(px - 5, crownY);
    ctx.lineTo(px - 3.5, crownY - 6.5);
    ctx.lineTo(px - 2, crownY);
    // Right peaks
    ctx.moveTo(px + 2, crownY);
    ctx.lineTo(px + 3.5, crownY - 6.5);
    ctx.lineTo(px + 5, crownY);
    ctx.moveTo(px + 5, crownY);
    ctx.lineTo(px + 7, crownY - 5);
    ctx.lineTo(px + 8, crownY);
    ctx.fill();

    // Gleaming Jewels in the Crown
    ctx.fillStyle = jewelRed; // Center Ruby
    ctx.fillRect(px - 1.5, crownY + 1.5, 3, 2.5);
    ctx.fillStyle = jewelBlue; // Left Sapphire
    ctx.fillRect(px - 6, crownY + 1.5, 2.5, 2);
    ctx.fillStyle = '#22c55e'; // Right Emerald
    ctx.fillRect(px + 3.5, crownY + 1.5, 2.5, 2);

    // Crown Sparkle Glint
    const glint = (Math.sin(time * 0.008) + 1) / 2;
    if (glint > 0.7) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 1, crownY - 9, 2, 2);
    }

    // 7. Off-Hand: Royal Orb (보주 - Globus Cruciger)
    const orbX = px - 11 - (attackRatio * 2);
    const orbY = py - 11;
    ctx.fillStyle = goldArmor;
    ctx.beginPath();
    ctx.arc(orbX, orbY, 4.5, 0, Math.PI * 2);
    ctx.fill();
    // Golden cross on top of orb
    ctx.fillStyle = '#fde047';
    ctx.fillRect(orbX - 0.75, orbY - 7, 1.5, 4);
    ctx.fillRect(orbX - 2.5, orbY - 6, 5, 1.5);
    // Blue band around orb
    ctx.strokeStyle = jewelBlue;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(orbX, orbY, 4.5, 2, 0, 0, Math.PI * 2);
    ctx.stroke();

    // 8. Main Hand: The Royal Scepter (황금 홀 - Scepter)
    // Dynamic swing/cast when attacking
    const handX = px + 10;
    const handY = py - 10;
    const scepterAngle = attackRatio > 0
      ? -Math.PI * 0.65 + attackRatio * (Math.PI * 0.9)
      : -Math.PI * 0.32;
    const shaftLen = 25;
    const tipX = handX + Math.cos(scepterAngle) * shaftLen;
    const tipY = handY + Math.sin(scepterAngle) * shaftLen;

    // Golden Scepter Shaft
    ctx.strokeStyle = goldArmor;
    ctx.lineWidth = 3.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(handX, handY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    // Spiral gold filigree
    ctx.strokeStyle = '#fef08a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(handX + Math.cos(scepterAngle) * 6, handY + Math.sin(scepterAngle) * 6);
    ctx.lineTo(handX + Math.cos(scepterAngle) * 16, handY + Math.sin(scepterAngle) * 16);
    ctx.stroke();

    // Scepter Head (Sun Crest & Pulsing Royal Gem Orb)
    ctx.save();
    ctx.translate(tipX, tipY);
    ctx.rotate(scepterAngle + Math.PI / 2);

    // Ornate Golden Wings / Prongs
    ctx.fillStyle = goldArmor;
    ctx.beginPath();
    ctx.moveTo(-5, 2);
    ctx.lineTo(-7, -4);
    ctx.lineTo(-3, -2);
    ctx.lineTo(0, 0);
    ctx.lineTo(3, -2);
    ctx.lineTo(7, -4);
    ctx.lineTo(5, 2);
    ctx.closePath();
    ctx.fill();

    // Radiant Royal Orb / Crystal at Tip
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 10 + Math.sin(time * 0.01) * 4;
    ctx.fillStyle = '#38bdf8'; // Glowing Star Sapphire Orb
    ctx.beginPath();
    ctx.arc(0, -6, 5, 0, Math.PI * 2);
    ctx.fill();
    // Inner core
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, -6, 2, 0, Math.PI * 2);
    ctx.fill();

    // Golden Crown Finial on Scepter Top
    ctx.fillStyle = '#fde047';
    ctx.fillRect(-1.5, -13, 3, 3);
    ctx.fillRect(-3, -12, 6, 1.2);
    ctx.restore();

    // 9. Holy Arc & Radiance Waves when striking / casting
    if (attackRatio > 0.1) {
      ctx.save();
      const waveRadius = shaftLen + 6 + attackRatio * 8;
      ctx.strokeStyle = `rgba(251, 191, 36, ${0.9 * (1 - attackRatio * 0.3)})`;
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.arc(handX, handY, waveRadius, -Math.PI * 0.7, scepterAngle, false);
      ctx.stroke();

      ctx.strokeStyle = `rgba(255, 255, 255, 0.95)`;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Sparkle stars bursting from scepter
      ctx.fillStyle = '#fde047';
      for (let s = 0; s < 3; s++) {
        const starX = tipX + (Math.sin(time * 0.02 + s) * 8);
        const starY = tipY + (Math.cos(time * 0.02 + s) * 8);
        ctx.fillRect(starX, starY, 2, 2);
      }
      ctx.restore();
    }
  }

  /**
   * Novice / Underwear Human Body Renderer (대표님 지시: 남자는 팬티, 여자는 브래지어+팬티, 기본 황토색 속옷)
   */
  private drawUnderwearNovice(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    const isMoving = Math.abs(ent.vx) > 0.1 || Math.abs(ent.vy) > 0.1;
    const bob = isMoving ? Math.sin(time * 0.015) * 2 : 0;
    const by = sy + bob;

    const animTimer = ent.attackAnimTimer || 0;
    const attackProgress = animTimer > 0 ? Math.min(1, Math.max(0, animTimer / 0.28)) : 0;
    const attackRatio = animTimer > 0 ? Math.sin((1 - attackProgress) * Math.PI) : 0;
    const lunge = attackRatio > 0 ? this.getLungeOffset(ent.dir, attackRatio * 7) : { x: 0, y: 0 };
    const px = sx + lunge.x;
    const py = by + lunge.y;

    const app = ent.appearance;
    const isFemale = app?.gender === 'female';
    const skinColor = app?.skinColor || '#fde68a';
    const clothColor = '#b45309'; // 황토색 (Ocher Underwear)
    const clothTrim = '#92400e';
    const bodyShape = app?.bodyShape || 'normal';

    // Body Shape Width Delta
    const widthOffset = bodyShape === 'slim' ? -1.5 : bodyShape === 'muscular' ? 1.5 : bodyShape === 'heavy' ? 3.0 : 0;

    // 1. Bare Legs & Feet
    ctx.fillStyle = skinColor;
    const legW = bodyShape === 'heavy' ? 4.5 : bodyShape === 'slim' ? 3.0 : 3.8;
    ctx.fillRect(px - 5 - widthOffset * 0.5, py + 2, legW, 11);
    ctx.fillRect(px + 2 + widthOffset * 0.5, py + 2, legW, 11);

    // Bare feet
    ctx.fillStyle = skinColor;
    ctx.fillRect(px - 6 - widthOffset * 0.5, py + 11, legW + 1.5, 3.5);
    ctx.fillRect(px + 2 + widthOffset * 0.5, py + 11, legW + 1.5, 3.5);

    // 2. Underwear Bottoms (남녀 공통 황토색 팬티)
    ctx.fillStyle = clothColor;
    const waistW = (isFemale ? 14 : 16) + widthOffset * 2;
    ctx.fillRect(px - waistW / 2, py - 4, waistW, 8);
    // Underwear band
    ctx.fillStyle = clothTrim;
    ctx.fillRect(px - waistW / 2, py - 5, waistW, 2);

    // 3. Torso / Chest (Bare Skin)
    ctx.fillStyle = skinColor;
    const torsoW = (isFemale ? 13 : 16) + widthOffset * 2;
    ctx.fillRect(px - torsoW / 2, py - 20, torsoW, 16);

    // 4. Female Bra Top (여성 캐릭터 전용 황토색 브래지어)
    if (isFemale) {
      ctx.fillStyle = clothColor;
      // Bra Cups
      ctx.beginPath();
      ctx.ellipse(px - 3.8, py - 13, 3.8, 3.2, 0, 0, Math.PI * 2);
      ctx.ellipse(px + 3.8, py - 13, 3.8, 3.2, 0, 0, Math.PI * 2);
      ctx.fill();
      // Bra Straps
      ctx.strokeStyle = clothTrim;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(px - 4, py - 16);
      ctx.lineTo(px - 4, py - 20);
      ctx.moveTo(px + 4, py - 16);
      ctx.lineTo(px + 4, py - 20);
      ctx.moveTo(px - 6, py - 12);
      ctx.lineTo(px + 6, py - 12);
      ctx.stroke();
    } else {
      // Male Pectorals definition
      ctx.strokeStyle = 'rgba(180, 83, 9, 0.25)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(px - 5, py - 14);
      ctx.lineTo(px, py - 12);
      ctx.lineTo(px + 5, py - 14);
      ctx.stroke();
    }

    // 5. Bare Arms & Hands
    ctx.fillStyle = skinColor;
    const armW = bodyShape === 'heavy' || bodyShape === 'muscular' ? 4 : 3;
    ctx.fillRect(px - torsoW / 2 - armW + 0.5, py - 18, armW, 14);
    ctx.fillRect(px + torsoW / 2 - 0.5, py - 18, armW, 14);

    // 6. Head, Eyes & Hair
    this.drawCustomHeadAndHair(ctx, ent, px, py);

    // 7. Novice Wooden Practice Stick (초보자 목검)
    const handX = px + torsoW / 2 + 3;
    const handY = py - 8;
    const swingAngle = attackRatio > 0
      ? -Math.PI * 0.55 + attackRatio * (Math.PI * 0.85)
      : -Math.PI * 0.28;
    const stickLen = 16;
    const tipX = handX + Math.cos(swingAngle) * stickLen;
    const tipY = handY + Math.sin(swingAngle) * stickLen;

    ctx.strokeStyle = '#b45309';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(handX, handY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
  }

  /**
   * Arthur - Paladin (Full Silver/Gold Plate Armor, Plumed Helm, Sword & Shield)
   */
  private drawArthurPaladin(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    // Walking bob
    const isMoving = Math.abs(ent.vx) > 0.1 || Math.abs(ent.vy) > 0.1;
    const bob = isMoving ? Math.sin(time * 0.015) * 2 : 0;
    const by = sy + bob;

    // Attack Motion Dynamics
    const animTimer = ent.attackAnimTimer || 0;
    const attackProgress = animTimer > 0 ? Math.min(1, Math.max(0, animTimer / 0.28)) : 0;
    const attackRatio = animTimer > 0 ? Math.sin((1 - attackProgress) * Math.PI) : 0;

    // Lunge forward in direction facing
    const lunge = attackRatio > 0 ? this.getLungeOffset(ent.dir, attackRatio * 7) : { x: 0, y: 0 };
    const px = sx + lunge.x;
    const py = by + lunge.y;

    const app = ent.appearance;
    const capeColor = app?.capeColor || '#1e3a8a';
    const primaryColor = app?.primaryArmorColor || '#cbd5e1';
    const secondaryColor = app?.secondaryArmorColor || '#2563eb';
    const showHelm = app?.showHelm !== false;

    // 1. Royal Cape
    ctx.fillStyle = capeColor;
    ctx.fillRect(px - 12, py - 24, 24, 28);

    // 2. Armored Greaves & Boots
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(px - 7, py + 4, 6, 9);
    ctx.fillRect(px + 2, py + 4, 6, 9);
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(px - 8, py + 11, 7, 4);
    ctx.fillRect(px + 2, py + 11, 7, 4);

    // 3. Plate Cuirass & Golden Heraldic Cross
    ctx.fillStyle = primaryColor;
    ctx.fillRect(px - 9, py - 24, 18, 24);
    ctx.fillStyle = '#f59e0b'; // Gold Cross
    ctx.fillRect(px - 2, py - 20, 4, 16);
    ctx.fillRect(px - 7, py - 14, 14, 4);

    // 4. Head: Custom Face/Hair OR Silver Great Helmet with Plume
    this.drawCustomHeadAndHair(ctx, ent, px, py - 5);
    if (showHelm) {
      ctx.fillStyle = primaryColor;
      ctx.fillRect(px - 7, py - 38, 14, 14);
      ctx.fillStyle = '#334155'; // Visor slit
      ctx.fillRect(px - 5, py - 33, 10, 4);
      ctx.fillStyle = capeColor; // Knight Plume matches cape
      ctx.fillRect(px - 3, py - 44, 6, 6);
    }

    // 5. Broadsword (Main Hand) with Dynamic Slash Swing
    const handX = px + 10;
    const handY = py - 10;
    const swingAngle = attackRatio > 0 
      ? -Math.PI * 0.55 + attackRatio * (Math.PI * 0.85) 
      : -Math.PI * 0.28;
    const bladeLen = 22;
    const tipX = handX + Math.cos(swingAngle) * bladeLen;
    const tipY = handY + Math.sin(swingAngle) * bladeLen;

    // Sword Blade
    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(handX, handY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    // Gold Crossguard
    const guardAngle = swingAngle + Math.PI / 2;
    const guardMidX = handX + Math.cos(swingAngle) * 5;
    const guardMidY = handY + Math.sin(swingAngle) * 5;
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(guardMidX - Math.cos(guardAngle) * 4, guardMidY - Math.sin(guardAngle) * 4);
    ctx.lineTo(guardMidX + Math.cos(guardAngle) * 4, guardMidY + Math.sin(guardAngle) * 4);
    ctx.stroke();

    // Golden / Silver Slash Arc Trajectory Trail during attack
    if (attackRatio > 0.15) {
      ctx.save();
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.75)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(handX, handY, bladeLen + 2, -Math.PI * 0.6, swingAngle, false);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }

    // 6. Knight Heater Shield (Off Hand)
    const shieldX = px - 11 - (attackRatio * 2);
    const shieldY = py - 12;
    ctx.fillStyle = secondaryColor;
    ctx.beginPath();
    ctx.arc(shieldX, shieldY, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f59e0b'; // Shield gold emblem
    ctx.fillRect(shieldX - 2, shieldY - 4, 4, 8);
  }

  /**
   * Companions: Scout (Caelen), Wizard (Eldryn), Cleric (Lyra), Bard (Robin), Friar (Tuck)
   */
  private drawCompanion(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    const isMoving = Math.abs(ent.vx) > 0.1 || Math.abs(ent.vy) > 0.1;
    const bob = isMoving ? Math.sin(time * 0.015) * 2 : 0;
    const by = sy + bob;

    // Attack Motion Dynamics
    const animTimer = ent.attackAnimTimer || 0;
    const attackProgress = animTimer > 0 ? Math.min(1, Math.max(0, animTimer / 0.28)) : 0;
    const attackRatio = animTimer > 0 ? Math.sin((1 - attackProgress) * Math.PI) : 0;

    // Lunge forward in direction facing
    const lunge = attackRatio > 0 ? this.getLungeOffset(ent.dir, attackRatio * 7) : { x: 0, y: 0 };
    const px = sx + lunge.x;
    const py = by + lunge.y;

    const app = ent.appearance;
    const showHelm = app?.showHelm !== false;

    if (ent.role === 'ranged_dps') {
      // === CAELEN / SCOUT (명사수) ===
      const capeCol = app?.capeColor || '#14532d';
      const primaryCol = app?.primaryArmorColor || '#15803d';
      const secCol = app?.secondaryArmorColor || '#92400e';

      // Deep Forest Cloak
      ctx.fillStyle = capeCol;
      ctx.fillRect(px - 10, py - 22, 20, 24);
      // Boots
      ctx.fillStyle = '#3f2d20';
      ctx.fillRect(px - 5, py - 2, 4, 14);
      ctx.fillRect(px + 1, py - 2, 4, 14);
      // Tunic
      ctx.fillStyle = primaryCol;
      ctx.fillRect(px - 8, py - 18, 16, 18);
      // Quiver on Back & Arrows
      ctx.fillStyle = secCol;
      ctx.fillRect(px + 7, py - 24, 5, 14);
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(px + 8, py - 27, 3, 3);
      // Custom Head & Hair
      this.drawCustomHeadAndHair(ctx, ent, px, py);
      if (showHelm) {
        ctx.fillStyle = capeCol; // Hood
        ctx.beginPath();
        ctx.moveTo(px - 8, py - 24);
        ctx.lineTo(px, py - 36);
        ctx.lineTo(px + 8, py - 24);
        ctx.closePath();
        ctx.fill();
      }

      // Wooden Hunting Bow with Draw/Release Motion
      const bowX = px + 14;
      const bowY = py - 14;
      ctx.strokeStyle = '#b45309';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(bowX, bowY, 13, -Math.PI * 0.4, Math.PI * 0.4);
      ctx.stroke();

      // Dynamic Bowstring: Pulled back when preparing shot
      const stringPull = attackRatio * 7;
      ctx.strokeStyle = '#f8fafc';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(bowX + 4, bowY - 10);
      ctx.lineTo(bowX + 4 - stringPull, bowY);
      ctx.lineTo(bowX + 4, bowY + 10);
      ctx.stroke();

      // Nocked Arrow & Release Arrow Flash
      if (attackRatio > 0.1) {
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(bowX + 4 - stringPull, bowY);
        ctx.lineTo(bowX + 16, bowY);
        ctx.stroke();
        // Arrowhead tip
        ctx.fillStyle = '#94a3b8';
        ctx.beginPath();
        ctx.moveTo(bowX + 16, bowY - 2);
        ctx.lineTo(bowX + 20, bowY);
        ctx.lineTo(bowX + 16, bowY + 2);
        ctx.closePath();
        ctx.fill();

        // Release Wind Arc
        if (attackRatio > 0.4) {
          ctx.strokeStyle = 'rgba(254, 240, 138, 0.85)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(bowX + 12, bowY, 16, -0.4, 0.4);
          ctx.stroke();
        }
      }

    } else if (ent.role === 'caster_dps') {
      // === ELDRYN / WIZARD (비전 마법사) ===
      const primaryCol = app?.primaryArmorColor || '#312e81';
      const secCol = app?.secondaryArmorColor || '#fbbf24';
      const capeCol = app?.capeColor || '#1e1b4b';

      // Robe with Hem
      ctx.fillStyle = primaryCol;
      ctx.beginPath();
      ctx.moveTo(px - 8, py - 16);
      ctx.lineTo(px - 12, py + 10);
      ctx.lineTo(px + 12, py + 10);
      ctx.lineTo(px + 8, py - 16);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = secCol;
      ctx.fillRect(px - 12, py + 8, 24, 2.5);
      ctx.fillRect(px - 2, py - 16, 4, 25);

      // Custom Head, Hair & Facial Feature
      this.drawCustomHeadAndHair(ctx, ent, px, py);

      // Pointed Wizard Hat
      if (showHelm) {
        ctx.fillStyle = capeCol;
        ctx.beginPath();
        ctx.ellipse(px, py - 31, 13, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(px - 8, py - 31);
        ctx.lineTo(px - 2, py - 45);
        ctx.lineTo(px + 8, py - 31);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = secCol; // Star on hat
        ctx.fillRect(px - 2, py - 37, 4, 3);
      }

      // Arcane Crystal Staff with Cast Thrust & Swirling Magic Orb
      const staffLift = attackRatio * 10;
      const staffThrust = attackRatio * 6;
      const staffTopX = px + 13.5 + staffThrust;
      const staffTopY = py - 10 - staffLift;

      ctx.fillStyle = '#451a03';
      ctx.fillRect(px + 12 + staffThrust * 0.5, py - 8 - staffLift * 0.7, 3, 30);

      // Glowing Arcane Orb with Pulsing Size
      const orbRadius = 5 + attackRatio * 6;
      ctx.fillStyle = 'rgba(56, 189, 248, 0.95)';
      ctx.beginPath();
      ctx.arc(staffTopX, staffTopY, orbRadius, 0, Math.PI * 2);
      ctx.fill();

      // Arcane Rune Halo Pulse during cast
      if (attackRatio > 0.15) {
        ctx.strokeStyle = 'rgba(192, 132, 252, 0.85)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(staffTopX, staffTopY, orbRadius + 5, 0, Math.PI * 2);
        ctx.stroke();
      }

    } else if (ent.role === 'healer') {
      // === LYRA / CLERIC (클레릭) ===
      const primaryCol = app?.primaryArmorColor || '#f8fafc';
      const secCol = app?.secondaryArmorColor || '#f59e0b';

      // Pure Vestments
      ctx.fillStyle = primaryCol;
      ctx.beginPath();
      ctx.moveTo(px - 8, py - 16);
      ctx.lineTo(px - 11, py + 10);
      ctx.lineTo(px + 11, py + 10);
      ctx.lineTo(px + 8, py - 16);
      ctx.closePath();
      ctx.fill();
      // Stole (영대)
      ctx.fillStyle = secCol;
      ctx.fillRect(px - 7, py - 18, 4, 26);
      ctx.fillRect(px + 3, py - 18, 4, 26);
      // Holy Cross on chest
      ctx.fillStyle = '#d97706';
      ctx.fillRect(px - 3, py - 12, 6, 2);
      ctx.fillRect(px - 1, py - 15, 2, 8);

      // Custom Head & Hair
      this.drawCustomHeadAndHair(ctx, ent, px, py);

      // Circlet Tiara
      if (showHelm) {
        ctx.fillStyle = secCol;
        ctx.fillRect(px - 7, py - 31, 14, 3);
      }

      // Golden Divine Warhammer with Overhead Strike Motion
      const maceAngle = attackRatio > 0 
        ? -Math.PI * 0.5 + attackRatio * (Math.PI * 0.75) 
        : -Math.PI * 0.2;
      const maceLen = 20;
      const maceBaseX = px + 10;
      const maceBaseY = py - 4;
      const maceHeadX = maceBaseX + Math.cos(maceAngle) * maceLen;
      const maceHeadY = maceBaseY + Math.sin(maceAngle) * maceLen;

      // Shaft
      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(maceBaseX, maceBaseY);
      ctx.lineTo(maceHeadX, maceHeadY);
      ctx.stroke();

      // Heavy Gold Warhammer Head
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(maceHeadX - 4, maceHeadY - 4, 8, 8);
      ctx.fillStyle = '#fbbf24';
      ctx.fillRect(maceHeadX - 2, maceHeadY - 5, 4, 10);

      // Holy Golden Smite Flash during attack
      if (attackRatio > 0.2) {
        ctx.strokeStyle = 'rgba(251, 191, 36, 0.85)';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(maceBaseX, maceBaseY, maceLen + 4, -Math.PI * 0.55, maceAngle);
        ctx.stroke();
      }

    } else if (ent.role === 'buffer') {
      // === ROBIN / BARD (음유시인) ===
      const capeCol = app?.capeColor || '#dc2626';
      const primaryCol = app?.primaryArmorColor || '#065f46';
      const secCol = app?.secondaryArmorColor || '#ef4444';

      // Capelet
      ctx.fillStyle = capeCol;
      ctx.fillRect(px - 10, py - 20, 20, 22);
      // Tunic
      ctx.fillStyle = primaryCol;
      ctx.fillRect(px - 7, py - 18, 14, 16);

      // Custom Head & Hair
      this.drawCustomHeadAndHair(ctx, ent, px, py);

      // Feather Beret
      if (showHelm) {
        ctx.fillStyle = primaryCol;
        ctx.beginPath();
        ctx.ellipse(px, py - 31, 9, 5, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = secCol; // Feather
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(px - 3, py - 33);
        ctx.quadraticCurveTo(px - 8, py - 42, px - 2, py - 44);
        ctx.stroke();
      }

      // Wooden Lute in Hand with Rapid Strumming Vibration
      const strumOffset = attackRatio > 0 ? Math.sin(time * 0.06) * 3 : 0;
      ctx.fillStyle = '#b45309';
      ctx.beginPath();
      ctx.ellipse(px + 8 + strumOffset, py - 10, 7, 9, 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#78350f';
      ctx.fillRect(px + 2 + strumOffset, py - 20, 3, 12);

      // Multiple Floating Musical Notes & Sound Wave during attack
      ctx.fillStyle = '#ec4899';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('♫', px - 13, py - 28 + Math.sin(time * 0.005) * 3);

      if (attackRatio > 0.1) {
        ctx.fillStyle = '#f59e0b';
        ctx.font = 'bold 13px sans-serif';
        ctx.fillText('♪', px + 16, py - 18 - attackRatio * 10);
        ctx.fillStyle = '#a855f7';
        ctx.fillText('♬', px + 10, py - 28 - attackRatio * 14);

        // Soundwave Arc
        ctx.strokeStyle = 'rgba(236, 72, 153, 0.7)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px + 14, py - 10, 10 + attackRatio * 14, -0.7, 0.7);
        ctx.stroke();
      }

    } else if (ent.role === 'monk_hybrid') {
      // === TUCK / FRIAR (수도승) ===
      const primaryCol = app?.primaryArmorColor || '#713f12';
      const secCol = app?.secondaryArmorColor || '#f8fafc';

      // Traditional Habit
      ctx.fillStyle = primaryCol;
      ctx.beginPath();
      ctx.moveTo(px - 8, py - 18);
      ctx.lineTo(px - 11, py + 10);
      ctx.lineTo(px + 11, py + 10);
      ctx.lineTo(px + 8, py - 18);
      ctx.closePath();
      ctx.fill();
      // Rope Cinch
      ctx.strokeStyle = secCol;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(px - 9, py - 2);
      ctx.lineTo(px + 9, py - 2);
      ctx.stroke();

      // Custom Head & Hair (defaults to Tonsure)
      this.drawCustomHeadAndHair(ctx, ent, px, py);
      if (showHelm) {
        // Monk Bamboo/Straw Hat (삿갓/두건)
        ctx.fillStyle = '#d97706';
        ctx.beginPath();
        ctx.moveTo(px - 12, py - 29);
        ctx.lineTo(px, py - 39);
        ctx.lineTo(px + 12, py - 29);
        ctx.closePath();
        ctx.fill();
      }

      // Wooden Prayer Beads
      ctx.fillStyle = '#b45309';
      for (let b = -4; b <= 4; b += 2.5) {
        ctx.beginPath();
        ctx.arc(px + b, py - 19, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Combat Quarterstaff with Powerful Thrust & Ki Shockwave
      const thrustDist = attackRatio * 14;
      ctx.strokeStyle = '#451a03';
      ctx.lineWidth = 3.5;
      ctx.beginPath();
      ctx.moveTo(px + 12 + thrustDist, py - 26);
      ctx.lineTo(px - 10, py + 12);
      ctx.stroke();

      // Punch Wave / Ki Blast Ring
      if (attackRatio > 0.15) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(px + 14 + thrustDist, py - 24, 7 + attackRatio * 9, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  /**
   * Monsters: Mountain Troll (Hulking brute), Goblin (Agile thief)
   */
  private drawMonster(
    ctx: CanvasRenderingContext2D,
    ent: EntityState,
    sx: number,
    sy: number,
    time: number
  ): void {
    const isMoving = Math.abs(ent.vx) > 0.1 || Math.abs(ent.vy) > 0.1;
    const bob = isMoving ? Math.sin(time * 0.012) * 2 : 0;
    const by = sy + bob;

    // Monster Attack Motion Dynamics
    const animTimer = ent.attackAnimTimer || 0;
    const attackProgress = animTimer > 0 ? Math.min(1, Math.max(0, animTimer / 0.25)) : 0;
    const attackRatio = animTimer > 0 ? Math.sin((1 - attackProgress) * Math.PI) : 0;

    const lunge = attackRatio > 0 ? this.getLungeOffset(ent.dir, attackRatio * 8) : { x: 0, y: 0 };
    const px = sx + lunge.x;
    const py = by + lunge.y;

    if (ent.id.includes('troll')) {
      // === MOUNTAIN TROLL (산악 트롤) ===
      // Hulking Moss-Green Muscular Torso
      ctx.fillStyle = '#065f46';
      ctx.beginPath();
      ctx.ellipse(px, py - 12, 18, 16, 0, 0, Math.PI * 2);
      ctx.fill();
      // Leather Loincloth
      ctx.fillStyle = '#451a03';
      ctx.fillRect(px - 12, py + 2, 24, 10);
      // Thick Brute Legs
      ctx.fillStyle = '#047857';
      ctx.fillRect(px - 12, py + 8, 8, 12);
      ctx.fillRect(px + 4, py + 8, 8, 12);
      // Head & Underbite Fangs
      ctx.fillStyle = '#065f46';
      ctx.beginPath();
      ctx.arc(px, py - 26, 11, 0, Math.PI * 2);
      ctx.fill();
      // Glowing Red Brute Eyes
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(px - 5, py - 28, 3, 3);
      ctx.fillRect(px + 2, py - 28, 3, 3);
      // Yellow Horns
      ctx.fillStyle = '#fde047';
      ctx.beginPath();
      ctx.moveTo(px - 8, py - 32);
      ctx.lineTo(px - 14, py - 44);
      ctx.lineTo(px - 4, py - 34);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(px + 8, py - 32);
      ctx.lineTo(px + 14, py - 44);
      ctx.lineTo(px + 4, py - 34);
      ctx.closePath();
      ctx.fill();

      // Giant Studded Club with Overhead Slam Motion
      const clubAngle = attackRatio > 0 
        ? -Math.PI * 0.5 + attackRatio * (Math.PI * 0.7) 
        : -Math.PI * 0.25;
      const clubLen = 32;
      const clubBaseX = px + 12;
      const clubBaseY = py - 4;
      const clubHeadX = clubBaseX + Math.cos(clubAngle) * clubLen;
      const clubHeadY = clubBaseY + Math.sin(clubAngle) * clubLen;

      ctx.strokeStyle = '#78350f';
      ctx.lineWidth = 7;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(clubBaseX, clubBaseY);
      ctx.lineTo(clubHeadX, clubHeadY);
      ctx.stroke();

      // Club Spikes
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(clubHeadX - 2, clubHeadY - 2, 4, 4);

      // Heavy Ground Slam Dust Shockwave
      if (attackRatio > 0.3) {
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(clubHeadX, clubHeadY, 12, 0, Math.PI * 2);
        ctx.stroke();
      }

    } else if (ent.id.includes('slime')) {
      // === GREEN SLIME (녹색 슬라임) ===
      const squish = Math.sin(time * 0.01) * 3 + (attackRatio * 5);
      ctx.fillStyle = 'rgba(16, 185, 129, 0.85)';
      ctx.beginPath();
      ctx.ellipse(px, py + 4 - (attackRatio * 4), 14 + squish, 10 - squish * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(52, 211, 153, 0.9)';
      ctx.beginPath();
      ctx.ellipse(px - 3, py + 2 - (attackRatio * 4), 7, 5, -0.2, 0, Math.PI * 2);
      ctx.fill();
      // Eyes
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(px - 5, py + 1 - (attackRatio * 4), 3, 3);
      ctx.fillRect(px + 3, py + 1 - (attackRatio * 4), 3, 3);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px - 4, py + 1 - (attackRatio * 4), 1, 1);
      ctx.fillRect(px + 4, py + 1 - (attackRatio * 4), 1, 1);

    } else if (ent.id.includes('skeleton')) {
      // === SKELETON WARRIOR (스켈레톤 전사) ===
      // Ribcage
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(px - 6, py - 14, 12, 10);
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(px - 4, py - 12, 8, 2);
      ctx.fillRect(px - 4, py - 8, 8, 2);
      // Bony legs
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(px - 5, py - 4, 3, 14);
      ctx.fillRect(px + 2, py - 4, 3, 14);
      // Skull
      ctx.fillStyle = '#f1f5f9';
      ctx.beginPath();
      ctx.arc(px, py - 22, 7, 0, Math.PI * 2);
      ctx.fill();
      // Eye sockets
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(px - 4, py - 23, 2, 3);
      ctx.fillRect(px + 2, py - 23, 2, 3);

      // Rusted Sword with Dynamic Slash
      const skelAngle = attackRatio > 0 
        ? -Math.PI * 0.5 + attackRatio * (Math.PI * 0.8) 
        : -Math.PI * 0.3;
      const skelSwordLen = 18;
      const skelHandX = px + 7;
      const skelHandY = py - 8;
      const skelTipX = skelHandX + Math.cos(skelAngle) * skelSwordLen;
      const skelTipY = skelHandY + Math.sin(skelAngle) * skelSwordLen;

      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(skelHandX, skelHandY);
      ctx.lineTo(skelTipX, skelTipY);
      ctx.stroke();

      if (attackRatio > 0.2) {
        ctx.strokeStyle = 'rgba(203, 213, 225, 0.8)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(skelHandX, skelHandY, skelSwordLen, -Math.PI * 0.5, skelAngle);
        ctx.stroke();
      }

    } else if (ent.id.includes('dragon')) {
      // === ANCIENT FLAME DRAGON (고대 화염룡) ===
      // Massive shadow & fiery ground aura
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.beginPath();
      ctx.ellipse(px, py + 16, 56, 26, 0, 0, Math.PI * 2);
      ctx.fill();

      // Tail with Spikes
      ctx.strokeStyle = '#7f1d1d';
      ctx.lineWidth = 12;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(px - 15, py + 10);
      ctx.quadraticCurveTo(px - 45, py + 22, px - 58, py + 6);
      ctx.stroke();

      // Dragon Hind Legs
      ctx.fillStyle = '#991b1b';
      ctx.beginPath();
      ctx.ellipse(px - 24, py + 14, 12, 9, -0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(px + 24, py + 14, 12, 9, 0.2, 0, Math.PI * 2);
      ctx.fill();

      // Wings (Animated flapping)
      const flap = Math.sin(time * 0.004) * 8 + (attackRatio * 10);
      ctx.fillStyle = '#7f1d1d';
      // Left Wing
      ctx.beginPath();
      ctx.moveTo(px - 14, py - 24);
      ctx.lineTo(px - 65, py - 65 + flap);
      ctx.lineTo(px - 24, py - 12);
      ctx.closePath();
      ctx.fill();
      // Right Wing
      ctx.beginPath();
      ctx.moveTo(px + 14, py - 24);
      ctx.lineTo(px + 65, py - 65 + flap);
      ctx.lineTo(px + 24, py - 12);
      ctx.closePath();
      ctx.fill();

      // Muscular Red Body
      ctx.fillStyle = '#b91c1c';
      ctx.beginPath();
      ctx.ellipse(px, py - 14, 26, 22, 0, 0, Math.PI * 2);
      ctx.fill();

      // Dragon Head & Horns
      ctx.fillStyle = '#dc2626';
      ctx.beginPath();
      ctx.ellipse(px, py - 36, 16, 13, 0, 0, Math.PI * 2);
      ctx.fill();
      // Horns
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.moveTo(px - 10, py - 42);
      ctx.lineTo(px - 22, py - 58);
      ctx.lineTo(px - 5, py - 44);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(px + 10, py - 42);
      ctx.lineTo(px + 22, py - 58);
      ctx.lineTo(px + 5, py - 44);
      ctx.closePath();
      ctx.fill();
      // Fiery Eyes
      ctx.fillStyle = '#fde047';
      ctx.fillRect(px - 7, py - 38, 4, 4);
      ctx.fillRect(px + 3, py - 38, 4, 4);

      // Dragon Fire Breath Flash during attack
      if (attackRatio > 0.2) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.75)';
        ctx.beginPath();
        ctx.arc(px, py - 28, 12 + attackRatio * 16, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(251, 191, 36, 0.85)';
        ctx.beginPath();
        ctx.arc(px, py - 28, 6 + attackRatio * 8, 0, Math.PI * 2);
        ctx.fill();
      }

    } else {
      // === GOBLIN SKIRMISHER (고블린 정찰병) ===
      // Small hunched red/green goblin
      ctx.fillStyle = '#991b1b'; // Red leather tunic
      ctx.fillRect(px - 6, py - 14, 12, 12);
      // Skinny legs
      ctx.fillStyle = '#15803d';
      ctx.fillRect(px - 5, py - 2, 3, 9);
      ctx.fillRect(px + 2, py - 2, 3, 9);
      // Goblin Head & Pointed Ears
      ctx.fillStyle = '#16a34a';
      ctx.beginPath();
      ctx.arc(px, py - 19, 7, 0, Math.PI * 2);
      ctx.fill();
      // Left ear
      ctx.beginPath();
      ctx.moveTo(px - 6, py - 19);
      ctx.lineTo(px - 14, py - 23);
      ctx.lineTo(px - 5, py - 15);
      ctx.closePath();
      ctx.fill();
      // Right ear
      ctx.beginPath();
      ctx.moveTo(px + 6, py - 19);
      ctx.lineTo(px + 14, py - 23);
      ctx.lineTo(px + 5, py - 15);
      ctx.closePath();
      ctx.fill();
      // Shifty Yellow Eyes
      ctx.fillStyle = '#fde047';
      ctx.fillRect(px - 3, py - 20, 2, 2);
      ctx.fillRect(px + 1, py - 20, 2, 2);

      // Wicked Dagger with Rapid Thrusting Motion
      const stabOffset = attackRatio * 9;
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(px + 7 + stabOffset, py - 10);
      ctx.lineTo(px + 15 + stabOffset, py - 16);
      ctx.stroke();

      if (attackRatio > 0.2) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(px + 12 + stabOffset, py - 14);
        ctx.lineTo(px + 19 + stabOffset, py - 18);
        ctx.stroke();
      }
    }
  }

  /**
   * Draw World Objects (Harvesting trees, rock ores, herbs, campfires, furniture)
   */
  public drawWorldObject(
    ctx: CanvasRenderingContext2D,
    type: string,
    sx: number,
    sy: number,
    time: number
  ): void {
    if (type === 'tree') {
      // === TREE (고목나무) ===
      // Trunk
      ctx.fillStyle = '#3e230f';
      ctx.fillRect(sx - 7, sy - 16, 14, 28);
      ctx.fillStyle = '#261509';
      ctx.fillRect(sx - 2, sy - 14, 4, 24);
      // Foliage 3 layers
      ctx.fillStyle = '#143311';
      ctx.beginPath();
      ctx.arc(sx, sy - 32, 26, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#1b4d17';
      ctx.beginPath();
      ctx.arc(sx - 4, sy - 36, 20, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#276e21';
      ctx.beginPath();
      ctx.arc(sx - 6, sy - 40, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#4ade80';
      ctx.fillRect(sx - 10, sy - 42, 5, 4);
      ctx.fillRect(sx + 4, sy - 36, 5, 4);

    } else if (type === 'rock_ore') {
      // === IRON ORE (철광석 바위) ===
      ctx.fillStyle = '#373c47';
      ctx.beginPath();
      ctx.moveTo(sx - 18, sy + 6);
      ctx.lineTo(sx - 12, sy - 14);
      ctx.lineTo(sx + 6, sy - 18);
      ctx.lineTo(sx + 18, sy - 6);
      ctx.lineTo(sx + 14, sy + 10);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#505766';
      ctx.beginPath();
      ctx.moveTo(sx - 12, sy - 14);
      ctx.lineTo(sx + 6, sy - 18);
      ctx.lineTo(sx + 2, sy - 2);
      ctx.closePath();
      ctx.fill();
      // Golden Ore Vein Crystals
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 6, sy - 10, 6, 6);
      ctx.fillRect(sx + 6, sy - 4, 5, 6);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(sx - 4, sy - 8, 3, 3);

    } else if (type === 'herb_plant') {
      // === HERB PLANT (치유의 약초) ===
      ctx.fillStyle = '#15803d';
      ctx.beginPath();
      ctx.arc(sx - 5, sy + 2, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(sx + 5, sy + 3, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(sx, sy - 2, 7, 0, Math.PI * 2);
      ctx.fill();
      // Pink Healing Flower
      ctx.fillStyle = '#f43f5e';
      ctx.fillRect(sx - 4, sy - 6, 8, 7);
      ctx.fillStyle = '#fbcfe8';
      ctx.fillRect(sx - 2, sy - 4, 4, 3);

    } else if (type === 'campfire') {
      // === CAMPFIRE (모닥불) ===
      ctx.fillStyle = '#57534e';
      ctx.fillRect(sx - 12, sy + 2, 24, 6);
      const cPulse = (Math.sin(time * 0.01) + 1) * 0.5;
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(sx, sy - 2, 14 + cPulse * 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#f59e0b';
      ctx.beginPath();
      ctx.arc(sx, sy - 6, 9 + cPulse * 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(sx, sy - 9, 5, 0, Math.PI * 2);
      ctx.fill();

    } else if (type === 'dungeon_gate') {
      // === GOTHIC OBSIDIAN DUNGEON PORTAL GATE ===
      ctx.fillStyle = '#1e1b4b';
      ctx.fillRect(sx - 16, sy - 44, 32, 44);
      ctx.fillStyle = '#312e81';
      ctx.fillRect(sx - 12, sy - 40, 24, 40);

      // Swirling Purple Nether Vortex
      const portalGlow = (Math.sin(time * 0.008) + 1) * 0.5;
      const vGrad = ctx.createRadialGradient(sx, sy - 20, 2, sx, sy - 20, 16);
      vGrad.addColorStop(0, '#c084fc');
      vGrad.addColorStop(0.6, '#7e22ce');
      vGrad.addColorStop(1, '#3b0764');
      ctx.fillStyle = vGrad;
      ctx.beginPath();
      ctx.ellipse(sx, sy - 20, 10 + portalGlow * 3, 16, 0, 0, Math.PI * 2);
      ctx.fill();

      // Skull Crest on top
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 5, sy - 48, 10, 8);
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(sx - 3, sy - 46, 2, 2);
      ctx.fillRect(sx + 1, sy - 46, 2, 2);
      ctx.font = 'bold 11px sans-serif';
      ctx.fillStyle = '#fbcfe8';
      ctx.textAlign = 'center';
      ctx.fillText('💀 DUNGEON', sx, sy - 52);

    } else if (type === 'torch_pillar') {
      // === DUNGEON STONE TORCH PILLAR ===
      ctx.fillStyle = '#1e2430';
      ctx.fillRect(sx - 6, sy - 36, 12, 40);
      ctx.fillStyle = '#334155';
      ctx.fillRect(sx - 8, sy - 38, 16, 4);

      // Flame with flickering animation
      const fP = (Math.sin(time * 0.015) + 1) * 0.5;
      ctx.fillStyle = '#f97316';
      ctx.beginPath();
      ctx.arc(sx, sy - 42, 6 + fP * 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fef08a';
      ctx.beginPath();
      ctx.arc(sx, sy - 44, 3 + fP, 0, Math.PI * 2);
      ctx.fill();

    } else if (type === 'crypt_wall') {
      // === DARK GOTHIC CRYPT WALL ===
      const wallH = 46;
      const topY = sy - wallH;
      const hw = 32;
      const hh = 16;

      // Left shadow face
      ctx.fillStyle = '#161b24';
      ctx.beginPath();
      ctx.moveTo(sx - hw, sy);
      ctx.lineTo(sx, sy + hh);
      ctx.lineTo(sx, topY + hh);
      ctx.lineTo(sx - hw, topY);
      ctx.closePath();
      ctx.fill();

      // Right lit face
      ctx.fillStyle = '#222a38';
      ctx.beginPath();
      ctx.moveTo(sx, sy + hh);
      ctx.lineTo(sx + hw, sy);
      ctx.lineTo(sx + hw, topY);
      ctx.lineTo(sx, topY + hh);
      ctx.closePath();
      ctx.fill();

      // Top cap diamond
      ctx.fillStyle = '#2d3748';
      ctx.beginPath();
      ctx.moveTo(sx, topY - hh);
      ctx.lineTo(sx + hw, topY);
      ctx.lineTo(sx, topY + hh);
      ctx.lineTo(sx - hw, topY);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#1a202c';
      ctx.lineWidth = 1;
      ctx.stroke();

    } else if (type === 'bone_pile') {
      // === SKELETON BONE PILE ===
      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(sx - 8, sy - 2, 6, 6);
      ctx.fillRect(sx + 2, sy - 4, 6, 6);
      ctx.fillStyle = '#94a3b8';
      ctx.fillRect(sx - 3, sy - 6, 6, 6);
      ctx.fillStyle = '#475569';
      ctx.fillRect(sx - 2, sy - 4, 2, 2);
      ctx.fillRect(sx + 1, sy - 4, 2, 2);

    } else if (type === 'loot_chest') {
      // === EPIC DRAGON LOOT CHEST ===
      const bounce = Math.sin(time * 0.006) * 3;
      const itemY = sy - 12 + bounce;

      // Golden sparkling aura
      ctx.fillStyle = 'rgba(245, 158, 11, 0.35)';
      ctx.beginPath();
      ctx.arc(sx, itemY, 14, 0, Math.PI * 2);
      ctx.fill();

      // Chest body
      ctx.fillStyle = '#854d0e';
      ctx.fillRect(sx - 10, itemY - 6, 20, 12);
      ctx.fillStyle = '#f59e0b';
      ctx.fillRect(sx - 10, itemY - 2, 20, 3);
      ctx.fillStyle = '#fef08a';
      ctx.fillRect(sx - 2, itemY - 3, 4, 5);
    }
  }
}
